import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { DateRange } from 'react-day-picker';

import {
  DEFAULT_NEWS_PAGE_SIZE,
  fetchNews,
  FetchNewsFilters,
} from '@/lib/api/news';
import { ArticleSource } from '@/schema';

export interface UseNewsFeedFilters {
  symbols: string[];
  sources: ArticleSource[];
  dateRange?: DateRange;
  searchQuery: string;
  hasSymbolOnly: boolean;
}

export interface UseNewsFeedOptions {
  filters: UseNewsFeedFilters;
  limit?: number;
  enabled?: boolean;
}

export function useNewsFeed({
  filters,
  limit = DEFAULT_NEWS_PAGE_SIZE,
  enabled = true,
}: UseNewsFeedOptions) {
  const { symbols, sources, dateRange, searchQuery, hasSymbolOnly } = filters;
  const from = dateRange?.from?.getTime();
  const to = dateRange?.to?.getTime();

  const queryFilters: FetchNewsFilters = {
    symbols,
    sources,
    from,
    to,
    searchQuery,
    hasSymbolOnly,
  };

  const filtersKey = useMemo(
    () => ({ symbols, sources, from, to, searchQuery, hasSymbolOnly }),
    [symbols, sources, from, to, searchQuery, hasSymbolOnly]
  );

  const queryResult = useInfiniteQuery({
    queryKey: ['news', limit, filtersKey],
    queryFn: ({ pageParam = 1, signal }) =>
      fetchNews({
        page: pageParam as number,
        limit,
        filters: queryFilters,
        signal,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.news.length === limit ? allPages.length + 1 : undefined,
    staleTime: 1000,
    placeholderData: (prevData) => prevData,
    enabled,
  });

  const articles = useMemo(() => {
    const pages = queryResult.data?.pages ?? [];
    return pages.flatMap((page) => page.news);
  }, [queryResult.data]);

  return {
    ...queryResult,
    articles,
    hasMore: Boolean(queryResult.hasNextPage),
  };
}
